import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { remove } from 'lodash';
import { Box, FilterOptionsState, Grid2, Typography, createFilterOptions } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { createHospital, editHospital } from './_apiHospital';
import useValidationSchema from './_formHospital';
import { IHospital, IHospitalForm } from './_types';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import FormInput from 'components/Form/Input/Input';
import FormAutocomplete from 'components/Form/Autocomplete/Autocomplete';
import { Papeer } from 'components/Papeer/Papeer';
import useAlerts from 'components/Alerts/useAlerts';
import { EntityButtons } from 'components/Form/EntityButtons/EntityButtons';

const HospitalForm: React.FC<IHospitalForm> = ({
  toggleDialog,
  keyValues,
  id,
  row,
  loadEntity,
  facilities,
}) => {
  const { t } = useTranslation('Hospitals');
  const { compactMode } = useAppInfo();
  const { toggleLoader } = useAppGlobals();
  const { addErrorAlert, addSuccessAlert } = useAlerts();
  const [entity, setEntity] = useState<IHospital>();
  const [prepareKeyValues, setPrepareKeyValues] = useState<{
    identifiers: string[];
    names: string[];
  }>(keyValues);

  const { HospitalFormSchema } = useValidationSchema(prepareKeyValues);

  const methods = useForm<IHospital>({
    resolver: yupResolver(HospitalFormSchema),
    defaultValues: { identifier: '', name: '' },
  });
  const { handleSubmit, reset, register } = methods;

  const isCreating = id === 'create';

  const onSubmit = handleSubmit(async (values) => {
    toggleLoader();
    const fn = isCreating ? createHospital : editHospital;
    const response = await fn(values);
    if (response === true) {
      addSuccessAlert(t('saved'));
      loadEntity();
      toggleDialog(false);
    } else {
      addErrorAlert(t('errorSaving'));
    }
    toggleLoader(false);
  });

  const getEntity = async () => {
    toggleLoader();
    try {
      setEntity(row);
      reset(row);
    } catch (e) {
      console.debug(e);
    }
    toggleLoader(false);
  };

  useEffect(() => {
    getEntity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (entity) {
      const identifiers = [...keyValues.identifiers];
      const names = [...keyValues.names];
      remove(identifiers, (item) => item === entity.identifier);
      remove(names, (item) => item === entity.name);
      setPrepareKeyValues({ identifiers, names });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity]);

  // Možno zadat vlastní hodnotu
  const customFilterOption = (options: any[], params: FilterOptionsState<any>): any[] => {
    const filter = createFilterOptions<any>();
    const filtered = filter(options, params);
    const { inputValue } = params;
    const isExisting = options.some((option) => inputValue === option);
    if (inputValue !== '' && !isExisting) {
      filtered.push({ newValue: inputValue, label: `${t('StudyTags:create')} "${inputValue}"` });
    }
    return filtered;
  };

  return (
    <>
      {entity || isCreating ? (
        <FormProvider {...methods}>
          <form onSubmit={onSubmit}>
            <input {...register('id')} type="hidden" />
            <Grid2 container={true} spacing={0}>
              <Grid2 size={12}>
                <FormInput name="identifier" label={t('identifier')} type="text" required={true} />
              </Grid2>
              <Grid2 size={12}>
                <FormInput
                  name="alternativeIdentifier"
                  label={t('alternativeIdentifier')}
                  type="text"
                  required={true}
                />
              </Grid2>
              <Grid2 size={12}>
                <FormAutocomplete
                  name={`name`}
                  label={t('name')}
                  options={facilities}
                  renderOption={(props: any, option: any) => {
                    return (
                      <Box component="li" {...props}>
                        {option?.label ? option.label : option}
                      </Box>
                    );
                  }}
                  placeholder={t('name')}
                  required={true}
                  // Custom filter option or default
                  {...(customFilterOption ? { customFilterOptions: customFilterOption } : {})}
                  optionsIsStringArray={true}
                  trigger={methods.trigger}
                />
              </Grid2>
            </Grid2>
            <EntityButtons
              toggleDialog={toggleDialog}
              otherStyles={{ marginTop: compactMode ? 8 : 16 }}
            />
          </form>
        </FormProvider>
      ) : (
        <Papeer>
          <Typography>{t('hospitalNotFound')}</Typography>
        </Papeer>
      )}
    </>
  );
};

export default HospitalForm;
